"use client";

import { ChevronRight } from "lucide-react";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import type { ArticleItem } from "./ArticleCard";
import { resolveLinkHref } from "../../utils/links";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import {
  getImageObjectPositionStyle,
  getImageOverrideProps,
  mergeClassNames,
  mergeStyles,
  resolveImageValue,
} from "../../utils/imageOptions";

/**
 * Compact news-website article card used by `LatestArticleGrid` when
 * `cardStyle === "compact"`. Built to read as a "related articles" /
 * "À lire aussi" footer on an article page : the call sites where the
 * full `ArticleCard` (category pill + duplicated hero-as-avatar + three
 * placeholder social icons) reads as too much chrome.
 *
 * Layout pinned by visible-design choice:
 *   - hero image full-width at the top
 *   - title (brand blue, bold) immediately under, clickable
 *   - 3-line excerpt clamp for visual rhythm : falls through silently
 *     when the source has no `summary`
 *   - bottom row: `author · date` inline grey meta + right-aligned
 *     chevron-in-circle as the navigation affordance; omitted entirely
 *     when the card has no meta (the chevron joins the title row)
 *
 * Reuses the same `ArticleItem` type as `ArticleCard` so the
 * projector's existing field mapping (`title`, `excerpt`, `author`,
 * `date`, `image`, `href`) keeps working : only `category`,
 * `authorRole`, and the social-icon placeholders are intentionally
 * unused here.
 */
export function ArticleCardCompact({
  title,
  excerpt,
  author,
  date,
  href,
  imageSrc,
  value,
  valueUnit,
  values,
  portrait,
  imagePosition,
  imageZoom,
  titleNode,
  showDate = true,
}: ArticleItem & {
  /** Pre-rendered title (e.g. search-highlighted <mark> nodes) shown INSTEAD
   *  of `title`. String logic (hasTitle, date promotion) stays on `title`. */
  titleNode?: import("react").ReactNode;
  /** Portrait image ratio (`aspect-[4/5]`) instead of the wide fixed `h-56` band.
   *  Set ONLY by the gouverneur "Historique récent" strip so governor portraits read
   *  well in the narrower 4-up grid; every other compact strip keeps the wide image. */
  portrait?: boolean;
  /** CSS `object-position` for the portrait crop (face-biased). Passed by the
   *  gouverneur strip so the same focal point used on the detail-page hero keeps the
   *  face visible inside the narrow card. A stored CMS focal point still wins. */
  imagePosition?: string;
  /** In-frame zoom (transform scale) for the portrait crop. Passed by the gouverneur
   *  strip so a full-body source photo reads as an upper-body portrait, matching the
   *  detail-page hero. `undefined`/`1` = no zoom. Contained by the link's overflow. */
  imageZoom?: number;
  /** `false` drops the grey date from the meta row. Set by the "Historique récent"
   *  strip of the publication listings, whose cards carry the year in their title and
   *  where the bank does not want a second, publication-date reading. The date-as-title
   *  fallback below is NOT affected: a record with no title would otherwise render an
   *  empty card. Editorial strips ("À lire aussi" on an article) keep their date. */
  showDate?: boolean;
}) {
  const locale = useLocale();
  const { src, options } = resolveImageValue(imageSrc);
  // No silent placeholder fallback: data-record histories (cours-de-change,
  // secteur-extérieur, etc.) carry no editorial image. Rendering a generic
  // welcome-banner PNG for every card made the "Historique récent" grid look
  // broken. When there is no real image, render an image-less card : a tile
  // built around title + meta + chevron, sized to read as part of the same
  // grid as image cards.
  const hasImage = Boolean(src);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  // Face-biased crop base for portrait (governor) cards; a stored CMS focal point
  // still overrides it (merged after). No effect on the wide non-portrait cards.
  const portraitPositionStyle = imagePosition ? { objectPosition: imagePosition } : undefined;
  // Zoom a full-body governor portrait into its head+torso so the small card matches
  // the detail-page hero (clipped by the link's overflow-hidden). Overrides the tiny
  // hover-scale for those cards only : an acceptable trade for consistent framing.
  const portraitZoomStyle =
    imageZoom && imageZoom !== 1
      ? { transform: `scale(${imageZoom})`, transformOrigin: imagePosition || "center" }
      : undefined;
  const safeHref = resolveLinkHref(href) ?? "/";
  const valeur = (value ?? "").toString().trim();
  // Fiches a PLUSIEURS chiffres : les cinq maturites d'un encours de Bons BCC se
  // rangent sous le titre, une par ligne, avec leur libelle et leur unite.
  const valeurs = Array.isArray(values) ? values.filter((v) => v && v.value) : [];
  const hasExcerpt = Boolean(excerpt && excerpt.trim().length > 0);
  // Demande client (revue 07/2026) : l'auteur n'apparaît sur AUCUNE surface publique : la
  // donnée reste dans le payload (admin, SEO) mais la carte ne la rend jamais.
  void author;
  const hasAuthor = false;
  // Normalise the editorial date to one consistent localized style so a listing
  // never shows "2026-02-06" next to "21 Nov 2025"; non-dates pass through.
  const displayDate = formatDisplayDate(date, locale);
  const hasRealDate = Boolean(displayDate && displayDate.trim().length > 0);
  // `showDate === false` silences the meta-row date only. `dateAsTitle` below still
  // reads the real date, so a title-less record keeps a legible card.
  const hasDate = hasRealDate && showDate !== false;
  const hasTitle = Boolean(title && title.trim().length > 0);
  // Data-record histories (cours-de-change, notes de conjoncture…) surface
  // entries that carry ONLY a date : no editorial title or author. Rendering
  // that date as tiny grey meta made the card look empty, so promote it to
  // the title slot with the same treatment as a real title.
  const dateAsTitle = !hasTitle && !hasAuthor && hasRealDate;
  // The meta row only earns its footprint when it has meta to show. When the
  // date was promoted to the title slot (or the card has no meta at all) the
  // row would hold nothing but the chevron : so drop it and inline the
  // chevron on the title row instead.
  const showMetaRow = hasAuthor || (hasDate && !dateAsTitle);
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-md border border-bcc-panel bg-white shadow-bcc-panel transition hover:border-[#005CA9]/40">
      {hasImage ? (
        <RuntimeLink href={safeHref} className="block overflow-hidden bg-slate-100">
          <RuntimeImage
            src={src as string}
            alt=""
            className={mergeClassNames(
              portrait
                ? "aspect-[4/5] w-full object-cover transition group-hover:scale-[1.02]"
                : "h-56 w-full object-cover transition group-hover:scale-[1.02]",
              overrideProps.className,
            )}
            style={mergeStyles(portraitPositionStyle, portraitZoomStyle, objectPositionStyle, overrideProps.style)}
          />
        </RuntimeLink>
      ) : null}
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-2">
          <RuntimeLink
            href={safeHref}
            className="block min-w-0 flex-1 text-[18px] font-semibold leading-6 text-[#005CA9] hover:underline"
          >
            {dateAsTitle ? displayDate : (titleNode ?? title)}
          </RuntimeLink>
          {!showMetaRow ? (
            <RuntimeLink
              href={safeHref}
              aria-label={t(locale, "readArticle")}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#005CA9] transition hover:bg-[#EAF6FF]"
            >
              <ChevronRight className="h-4 w-4" />
            </RuntimeLink>
          ) : null}
        </div>
        {hasExcerpt ? (
          <p className="mt-2 line-clamp-3 text-sm leading-5 text-slate-600">{excerpt}</p>
        ) : null}
        {/* Le chiffre de la série, calé à GAUCHE sous le titre et le sous-titre :
            dans cette bande, le chevron tient déjà le coin droit, et une valeur
            poussée à droite se lisait en diagonale du reste de la carte. Gris
            ardoise et non bleu nuit : en gras, le ton du corps de texte passait
            avant le titre. Sans ligne de méta, il prend le bas de la carte pour que
            trois cartes voisines gardent la même hauteur de lecture. */}
        {valeur ? (
          <p
            className={`${showMetaRow ? "mt-2" : "mt-auto pt-3"} text-[18px] font-bold tabular-nums leading-tight text-[#5D6E8C]`}
          >
            {valeur}
            {valueUnit ? (
              <span className="ml-1 text-[13px] font-semibold text-[#6B7A99]">{valueUnit}</span>
            ) : null}
          </p>
        ) : null}
        {valeurs.length ? (
          <dl className={`${showMetaRow ? "mt-2" : "mt-auto pt-3"} space-y-1 border-t border-bcc-panel pt-2`}>
            {valeurs.map((v, i) => (
              <div key={`${v.label}-${i}`} className="flex items-baseline justify-between gap-3">
                <dt className="shrink-0 text-[12px] font-medium text-[#6B7A99]">{v.label}</dt>
                <dd className="text-right text-[13px] font-medium tabular-nums text-[#5D6E8C]">
                  {v.value}
                  {v.unit ? <span className="ml-1 font-medium text-[#6B7A99]">{v.unit}</span> : null}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        {showMetaRow ? (
          <div className="mt-auto flex items-center gap-1 pt-3 text-xs text-slate-500">
            {hasAuthor ? <span className="font-medium text-slate-700">{author}</span> : null}
            {hasAuthor && hasDate ? <span aria-hidden="true">·</span> : null}
            {hasDate && !dateAsTitle ? <span>{displayDate}</span> : null}
            <RuntimeLink
              href={safeHref}
              aria-label={t(locale, "readArticle")}
              className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-full text-[#005CA9] transition hover:bg-[#EAF6FF]"
            >
              <ChevronRight className="h-4 w-4" />
            </RuntimeLink>
          </div>
        ) : null}
      </div>
    </article>
  );
}
